import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC } from "react";
import useResponsive from "../../hooks/useResponsive";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { PostJobProps, PostJobPropsType } from "../../interface/job_board";
import dayjs, { Dayjs } from "dayjs";
import { PATH_JOBBOARD } from "../../routes/path";
import { useNotificationService } from "../notification";
import { LoadingButton } from "@mui/lab";
import { formatDate, formatTextType } from "../../utils/url-id";
import { validataPostJobForm } from "../../utils/validations";
import { apiPostJobWithOutsourcing } from "../../api/jobs";

export const labelSty = {
  fontFamily: "DM Sans",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "20px",
  letterSpacing: "-0.23999999463558197px",
  textAlign: "left",
  color: "#404E57",
  marginBottom: 1,
};

const PreviewJob: FC<{
  type: string;
  data: PostJobProps;
  date: Dayjs | null;
  benefits: string[];
  documents: string[];
  handlePreview: () => void;
  contractDuration: { startDate: string; endDate: string };
  isOutsourced: boolean;
  outsourcingDetails: {
    positions_needed: number;
    urgency: "low" | "medium" | "high" | "critical";
    deadline?: string;
    special_requirements?: string;
    budget_range?: {
      min?: number;
      max?: number;
    };
  };
}> = ({
  type,
  data,
  date,
  documents,
  benefits,
  handlePreview,
  contractDuration,
  isOutsourced,
  outsourcingDetails,
}) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();
  const isMedium = useResponsive("down", "md", "xl");

  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };

  const textSty = {
    fontFamily: "DM Sans",
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "18px",
    textAlign: "left",
  };

  const mutation = useMutation({
    mutationFn: (fieldValues: any) => apiPostJobWithOutsourcing(fieldValues),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        navigate(PATH_JOBBOARD.root);
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      showNotification("Process failed", "error");
    },
  });

  const handleJobPost = () => {
    let application_deadline;
    if (dayjs(date).isValid() && dayjs(date).isAfter(dayjs(), "day")) {
      application_deadline = dayjs(date).toISOString();
    } else {
      console.error("Invalid date provided for application deadline.");
    }

    if (benefits.length === 0) {
      showNotification("Additional Benefits is required", "error");
      return false;
    }

    const form_data = {
      ...data,
      job_type: type,
      required_documents: documents,
      salary_range: data.salary,
      application_deadline: application_deadline,
      contract_duration:
        type === "full time"
          ? ""
          : `${contractDuration.startDate} - ${contractDuration.endDate}`,
      is_outsourced: isOutsourced,
      outsourcing_details: isOutsourced ? outsourcingDetails : undefined,
    };

    const check_value = validataPostJobForm(form_data, showNotification);
    if (check_value) {
      mutation.mutate(form_data);
    }
  };

  return (
    <Box sx={{}}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle1">
          Post a Job
        </Typography>
        <Stack direction={"row"}>
          <Button
            onClick={handlePreview}
            sx={{ borderRadius: "14px", width: 100, fontWeight: 700 }}
          >
            Back
          </Button>

          <LoadingButton
            onClick={handleJobPost}
            loading={mutation.isPending}
            type="submit"
            variant="contained"
            sx={{
              borderRadius: "13px",
              fontFamily: "DM Sans",
              fontWeight: 700,
              width: 100,
            }}
          >
            Post
          </LoadingButton>
        </Stack>
      </Stack>

      {/* Outsourcing Summary */}
      {isOutsourced && (
        <Box
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "12px",
            backgroundColor: "#E3F2FD",
            border: "2px solid #2196F3",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "Lexend",
                fontWeight: 600,
                color: "#1976D2",
              }}
            >
              Outsourced Hiring
            </Typography>
            <Box
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: "16px",
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#1976D2",
                  textTransform: "uppercase",
                }}
              >
                {outsourcingDetails.urgency}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Typography sx={{ ...textSty }}>Positions Needed:</Typography>
              <Typography sx={{ ...labelSty }}>
                {outsourcingDetails.positions_needed}
              </Typography>
            </Stack>
            {outsourcingDetails.special_requirements && (
              <Stack direction="row" spacing={1}>
                <Typography sx={{ ...textSty }}>
                  Special Requirements:
                </Typography>
                <Typography sx={{ ...labelSty }}>
                  {outsourcingDetails.special_requirements}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      )}

      <Typography sx={{ ...headerSty, my: 2 }} variant="subtitle1">
        Job Type
      </Typography>
      <Box>
        <Stack direction={"row"} sx={{ gap: 2, my: 2 }}>
          <Typography sx={{ ...textSty, mb: 0 }}>Job Type</Typography>
          <Typography sx={{ ...labelSty }}>{formatTextType(type)}</Typography>
        </Stack>
        <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle1">
          Job Details
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Job Title</Typography>
            <Typography sx={{ ...labelSty }}>{data?.job_title}</Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Department</Typography>
            <Typography sx={{ ...labelSty }}> {data?.department}</Typography>
          </Stack>
        </Stack>
        <Stack sx={{ width: "100%", my: 2 }}>
          <Typography sx={{ ...textSty, mb: 0 }}>Job Description:</Typography>
          <Typography sx={{ ...labelSty }}>{data?.job_description}</Typography>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Qualifications</Typography>
            <Typography sx={{ ...labelSty }}>{data?.qualifications}</Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Experience Level:
            </Typography>
            <Typography sx={{ ...labelSty }}>
              {formatTextType(data?.experience_level)}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Employment Type & Schedule
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Employment Type:</Typography>
            <Typography sx={{ ...labelSty }}>
              {formatTextType(data?.employment_type)}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Work Schedule:</Typography>
            <Typography sx={{ ...labelSty }}>{data?.work_schedule}</Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          {type === "temporary" && (
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...textSty, mb: 0 }}>
                Contract Duration (For Temporary Jobs):
              </Typography>
              <Typography sx={{ ...labelSty }}>
                {formatDate(contractDuration.startDate)} -{" "}
                {formatDate(contractDuration.endDate)}
              </Typography>
            </Stack>
          )}
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Work Type</Typography>
            <Typography sx={{ ...labelSty }}>{data?.work_type}</Typography>
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Location & Compensation
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Work Location:</Typography>
            <Typography sx={{ ...labelSty }}>{data?.work_location}</Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Salary Range:{" "}
              {type === "full time"
                ? "(monthly)"
                : data.employment_type === "part time"
                  ? "(daily)"
                  : "(monthly)"}
            </Typography>
            <Typography sx={{ ...labelSty }}>{data.salary}</Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Additional Benefits:
            </Typography>
            {benefits?.map((val, id) => (
              <Box
                key={id}
                sx={{ display: "flex", gap: 2, alignItems: "center" }}
              >
                <Typography sx={{ ...labelSty }}>{val}</Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Application Details
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Application Deadline:
            </Typography>
            <Typography sx={{ ...labelSty }}>
              {formatDate(date?.toString() ?? "")}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Required Documents:
            </Typography>
            {documents?.map((val, id) => (
              <Box
                key={id}
                sx={{ display: "flex", gap: 2, alignItems: "center" }}
              >
                <TaskAltIcon sx={{ fontSize: "15px" }} />
                <Typography sx={{ ...labelSty }}>{val}</Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: "100%" }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Job Application:</Typography>
            <Typography sx={{ ...labelSty }}>{data?.how_to_apply}</Typography>
          </Stack>
        </Stack>
        {data?.external_link && (
          <Stack
            direction={isMedium ? "column" : "row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ my: 3, gap: 2 }}
          >
            <Stack sx={{ width: "100%" }}>
              <Typography sx={{ ...textSty, mb: 0 }}>
                External Application Link:
              </Typography>
              <Typography sx={{ ...labelSty }}>{data.external_link}</Typography>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default PreviewJob;
